import { Box, Button, Card, Container, Flex, Icon, Image, Link, Text } from '@chakra-ui/react';
import { FaPhoneAlt } from 'react-icons/fa';

import { CreditFrozen } from '../../assets/Images';
import CreditBureauLinks from '../../components/CreditBureauLinks';
import Subtitle from '../../components/Subtitle';
import Title from '../../components/Title';
import TitleContainer from '../../components/TitleContainer';
import getSearchPhoneNumbers from '../../utils/getSearchPhoneNumbers';

interface Props {
  customerFrozenOrLockedCredit: boolean;
  cobuyerFrozenOrLockedCredit: boolean;
}

interface TextContent {
  title: string;
  description: string;
  instructions: string;
  helpText: string;
}

const getTextContent = (justCobuyerFrozenOrLocked: boolean): TextContent => ({
  title: justCobuyerFrozenOrLocked
    ? 'HOW YOUR CO-BUYER CAN UNFREEZE OR UNLOCK THEIR CREDIT'
    : 'HOW TO UNFREEZE OR UNLOCK YOUR CREDIT',
  description: justCobuyerFrozenOrLocked
    ? "Your co-buyer has a credit freeze or lock with one or more of the credit bureaus. For us to continue the loan approval process, they'll need to unfreeze or unlock their credit."
    : "You'll need to unfreeze or unlock your credit with the credit bureaus where you've placed the restriction. ",
  instructions: justCobuyerFrozenOrLocked
    ? 'The process is simple and can be done by contacting the credit bureaus directly. Below are links to help them get started:'
    : "Each bureau has a slightly different process, but don't worry, it's simple and quick. You can unfreeze or unlock your credit by either calling them or visiting their websites below:",
  helpText: `Once ${
    justCobuyerFrozenOrLocked ? 'their' : 'your'
  } credit is unfrozen or unlocked, we'll be able to continue the loan approval process. If you need assistance beforehand, give us a call at`,
});

const FrozenOrLockedCredit = ({
  customerFrozenOrLockedCredit,
  cobuyerFrozenOrLockedCredit,
}: Props) => {
  const phoneNumber = getSearchPhoneNumbers();

  const justCobuyerFrozenOrLocked = !customerFrozenOrLockedCredit && cobuyerFrozenOrLockedCredit;
  const textContent = getTextContent(justCobuyerFrozenOrLocked);

  return (
    <Container>
      <TitleContainer>
        <Title textAlign="center">
          Your {justCobuyerFrozenOrLocked ? "Co-Buyer's" : ''} Credit is Frozen or Locked
        </Title>
      </TitleContainer>
      <Subtitle mt={0} mb={5} textAlign="center">
        We will need {justCobuyerFrozenOrLocked ? 'them' : 'you'} to unfreeze or unlock it to
        proceed.
      </Subtitle>
      <Card
        border="1px solid"
        borderColor="gainsboro"
        shadow="md"
        px="30px"
        py="25px"
        alignItems={{ base: 'center', md: 'start' }}
      >
        <Flex flexDir={{ base: 'column', md: 'row' }} alignItems="center">
          <Image w="160px" src={CreditFrozen} />
          <Box>
            <Text
              mb={4}
              fontWeight="bold"
              fontSize="22px"
              textAlign={{ base: 'center', md: 'left' }}
            >
              {textContent.title}
            </Text>
            <Text fontSize={{ base: '14px', lg: '16px' }}>{textContent.description}</Text>
            <Text mt={2} fontSize={{ base: '14px', lg: '16px' }}>
              {textContent.instructions}
            </Text>
          </Box>
        </Flex>
        <CreditBureauLinks separateLinkFromIcon fontSize={{ base: '14px', lg: '16px' }} />
      </Card>
      <Flex
        borderRadius="15px"
        bgColor="lightSkyBlue"
        p={4}
        flexDir="column"
        textAlign="center"
        gap={4}
        mt={4}
        mb={10}
      >
        <Text fontSize="22px" fontWeight="bold">
          Need Help Getting Started?
        </Text>
        <Text>
          {textContent.helpText} <Link href={`tel:+1-${phoneNumber}`}>{phoneNumber}</Link>.
        </Text>
        <Button
          as={Link}
          variant="secondary"
          mx="auto"
          w="max-content"
          href={`tel:+1-${phoneNumber}`}
        >
          <Icon as={FaPhoneAlt} mr={3} boxSize="12px" />
          {`Call ${phoneNumber}`}
        </Button>
      </Flex>
    </Container>
  );
};

export default FrozenOrLockedCredit;
